import 'leaflet/dist/leaflet.css'
import './leaflet-marker-cluster-compat'
import 'leaflet.markercluster'

import { css, Global } from '@emotion/react'
import { focusBoxShadow, Theme, useTheme } from 'bold-ui'
import { useLogger } from 'hooks/useLogger'
import Leaflet from 'leaflet'
import { PMTiles, Source } from 'pmtiles'
import { leafletLayer, View } from 'protomaps-leaflet'
import { Dispatch, ReactNode, RefObject, SetStateAction, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ResizeObserver from 'resize-observer-polyfill'
import { noop } from 'util/functions'

import { LeafletMapErrorBoundary } from './LeafletMapErrorBoundary'
import {
  BRASIL_MAP_CENTER,
  BRASIL_MAP_MAX_BOUNDS,
  EMPTY_MAP_MARKERS,
  LeafletLayerUrl,
  MAP_MARKER_SIZE,
  MAPA_TILES_URL,
  MapMarkerModel,
} from './map-model'
import { observePoiMarkers } from './map-poi-markers'
import { LABEL_RULES, PAINT_RULES } from './map-style'
import { createObservedTileSource, TileSourceError } from './map-tile-source'
import { MapClusterMarker } from './MapClusterMarker'
import { MapFeedback } from './MapFeedback'
import { getClusterMarkerSize } from './utils-map'

interface LeafletMapProps {
  center?: [number, number]
  zoom?: number
  minZoom?: number
  maxBounds?: [[number, number], [number, number]]
  markers?: MapMarkerModel[]
  mapFeedback?: ReactNode
  clusterize?: boolean
  showLoading?: boolean
  topOverlayRef?: RefObject<HTMLElement>
}

interface MarkerPortal {
  id: string
  container: HTMLElement
  content: ReactNode
}

const DEFAULT_MARKER_SIZE = MAP_MARKER_SIZE
const FIT_MARKERS_PADDING: [number, number] = [48, 48]
const FIT_MARKERS_MAX_ZOOM = 16
const POI_MARKERS_PANE = 'poiMarkersPane'
const POI_MARKERS_PANE_Z_INDEX = '550'

export function LeafletMap(props: Readonly<LeafletMapProps>) {
  const {
    center = BRASIL_MAP_CENTER,
    zoom = 16,
    minZoom,
    maxBounds = BRASIL_MAP_MAX_BOUNDS,
    markers = EMPTY_MAP_MARKERS,
    mapFeedback,
    showLoading = false,
    clusterize = true,
    topOverlayRef,
  } = props

  const [topOverlayHeight, setTopOverlayHeight] = useState(0)

  const theme = useTheme()
  const styles = createStyles(theme, topOverlayHeight)
  const logger = useLogger()
  const loggerRef = useRef(logger)
  loggerRef.current = logger

  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<Leaflet.Map | null>(null)
  const markersLayerRef = useRef<Leaflet.LayerGroup | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isLoadingTiles, setIsLoadingTiles] = useState(false)
  const [tileError, setTileError] = useState<TileSourceError | null>(null)
  const [markerPortals, setMarkerPortals] = useState<MarkerPortal[]>([])
  const [poiMarkerPortals, setPoiMarkerPortals] = useState<MarkerPortal[]>([])

  const isMapLoading = isInitialLoading || isLoadingTiles || showLoading

  useEffect(() => {
    let isMounted = true

    const bounds = toLatLngBounds(maxBounds)
    const map = createMap(containerRef.current!, { center, zoom, bounds })
    mapRef.current = map
    markersLayerRef.current = createMarkersLayer(clusterize).addTo(map)
    const poiMarkersLayer = createPoiMarkersLayer(map).addTo(map)
    const poiLeafletMarkers = new Map<string, Leaflet.Marker>()

    const stopClampingMinZoom = clampMinZoomToBounds(map, bounds, minZoom)
    const stopWatchingContainerResize = watchResize(containerRef.current!, () =>
      map.invalidateSize({ debounceMoveend: true })
    )

    hideLeafletBranding(map)

    const tileSource = createObservedTileSource(MAPA_TILES_URL, (error) => {
      if (isMounted) setTileError(error)
    })
    const tileLayer = addBaseTileLayer(map, tileSource, theme.pallete.surface.background)
    const baseView = getBaseView(tileLayer)
    const stopObservingPoiMarkers = baseView
      ? observePoiMarkers(
          map,
          baseView,
          (poiMarkers) => syncPoiMarkers(poiMarkersLayer, poiLeafletMarkers, poiMarkers, setPoiMarkerPortals),
          (error) => loggerRef.current.error('[LeafletMap] erro ao consultar estabelecimentos', error)
        )
      : noop

    const stopWatchingInitialLoad = watchInitialLoad(tileLayer, () => setIsInitialLoading(false))
    const stopWatchingTileLoading = watchTileLoading(tileLayer, setIsLoadingTiles)

    return () => {
      isMounted = false
      stopWatchingInitialLoad()
      stopWatchingTileLoading()
      stopClampingMinZoom()
      stopObservingPoiMarkers()
      stopWatchingContainerResize()
      map.remove()
      mapRef.current = null
      markersLayerRef.current = null
      setPoiMarkerPortals([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    const markersLayer = markersLayerRef.current
    if (!map || !markersLayer) {
      return
    }

    const contentByLayerId = new Map<number, ReactNode>()
    const leafletMarkers = markers.map((marker) => {
      const leafletMarker = createLeafletMarker(marker)
      contentByLayerId.set(Leaflet.stamp(leafletMarker), marker.content)
      return leafletMarker
    })

    const onLayerAdd = (event: Leaflet.LayerEvent) => handleMarkerLayerAdd(event, contentByLayerId, setMarkerPortals)
    const onLayerRemove = (event: Leaflet.LayerEvent) => handleMarkerLayerRemove(event, setMarkerPortals)

    map.on('layeradd', onLayerAdd)
    map.on('layerremove', onLayerRemove)

    markersLayer.clearLayers()
    addMarkersToLayer(markersLayer, leafletMarkers)

    return () => {
      map.off('layeradd', onLayerAdd)
      map.off('layerremove', onLayerRemove)
      markersLayer.clearLayers()
      setMarkerPortals([])
    }
  }, [markers])

  useEffect(() => {
    const map = mapRef.current
    if (!map || markers.length === 0) {
      return
    }

    map.fitBounds(toMarkersBounds(markers), {
      padding: FIT_MARKERS_PADDING,
      paddingTopLeft: [FIT_MARKERS_PADDING[0], FIT_MARKERS_PADDING[1] + topOverlayHeight],
      maxZoom: FIT_MARKERS_MAX_ZOOM,
    })
  }, [markers, topOverlayHeight])

  useEffect(() => {
    const topOverlay = topOverlayRef?.current
    if (!topOverlay) {
      return
    }

    return watchResize(topOverlay, () => setTopOverlayHeight(topOverlay.offsetHeight))
  }, [topOverlayRef])

  return (
    <div css={styles.root}>
      <Global styles={styles.clusterMarkerFocus} />
      <div ref={containerRef} css={styles.map} />
      {markerPortals.map(({ id, container, content }) => createPortal(content, container, id))}
      {poiMarkerPortals.map(({ id, container, content }) => createPortal(content, container, id))}
      {!tileError && (
        <MapFeedback topOverlayHeight={topOverlayHeight} isLoading={isMapLoading}>
          {mapFeedback}
        </MapFeedback>
      )}
      <LeafletMapErrorBoundary error={tileError} />
    </div>
  )
}

const CLUSTER_KEY_ENTER = 'Enter'
const CLUSTER_KEY_SPACE = ' '

function createMarkersLayer(clusterize: boolean): Leaflet.LayerGroup {
  if (!clusterize) {
    return Leaflet.layerGroup()
  }

  const clusterLayer = Leaflet.markerClusterGroup({
    iconCreateFunction: (cluster) => {
      const size = getClusterMarkerSize(cluster.getChildCount())
      return Leaflet.divIcon({
        className: 'leaflet-map-cluster-marker',
        html: '',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      })
    },
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
  })

  clusterLayer.on('clusterkeypress', (event: Leaflet.LeafletEvent) => handleClusterKeyPress(clusterLayer, event))

  return clusterLayer
}

function handleClusterKeyPress(clusterLayer: Leaflet.MarkerClusterGroup, event: Leaflet.LeafletEvent): void {
  const originalEvent = (event as Leaflet.LeafletKeyboardEvent).originalEvent
  if (originalEvent.key !== CLUSTER_KEY_ENTER && originalEvent.key !== CLUSTER_KEY_SPACE) {
    return
  }

  const cluster = event.propagatedFrom as Leaflet.MarkerCluster
  focusFirstChildMarkerAfterClusterExpand(clusterLayer, cluster)

  if (originalEvent.key === CLUSTER_KEY_SPACE) {
    originalEvent.preventDefault()
    clusterLayer.fire('click', { layer: cluster })
  }
}

function focusFirstChildMarkerAfterClusterExpand(
  clusterLayer: Leaflet.MarkerClusterGroup,
  cluster: Leaflet.MarkerCluster
): void {
  const [firstChildMarker] = cluster.getAllChildMarkers()
  if (!firstChildMarker) {
    return
  }

  const onClusterExpanded = () => {
    clusterLayer.off('spiderfied', onClusterExpanded)
    clusterLayer.off('animationend', onClusterExpanded)
    focusVisibleMarkerElement(clusterLayer, firstChildMarker)
  }

  clusterLayer.on('spiderfied', onClusterExpanded)
  clusterLayer.on('animationend', onClusterExpanded)
}

function focusVisibleMarkerElement(clusterLayer: Leaflet.MarkerClusterGroup, marker: Leaflet.Marker): void {
  const visibleParent = clusterLayer.getVisibleParent(marker)
  if (!visibleParent) {
    return
  }

  const element = visibleParent.getElement()
  if (!element) {
    return
  }

  const focusTarget = isMarkerCluster(visibleParent) ? element : element.querySelector<HTMLButtonElement>('button')

  requestAnimationFrame(() => focusTarget?.focus())
}

function isMarkerCluster(layer: Leaflet.Layer): layer is Leaflet.MarkerCluster {
  return layer instanceof Leaflet.MarkerCluster
}

function isMarkerClusterGroup(layer: Leaflet.LayerGroup): layer is Leaflet.MarkerClusterGroup {
  return layer instanceof Leaflet.MarkerClusterGroup
}

function addMarkersToLayer(markersLayer: Leaflet.LayerGroup, leafletMarkers: Leaflet.Marker[]): void {
  if (isMarkerClusterGroup(markersLayer)) {
    markersLayer.addLayers(leafletMarkers)
  } else {
    leafletMarkers.forEach((leafletMarker) => markersLayer.addLayer(leafletMarker))
  }
}

function upsertMarkerPortal(portals: MarkerPortal[], portal: MarkerPortal): MarkerPortal[] {
  return [...portals.filter((existing) => existing.id !== portal.id), portal]
}

function removeMarkerPortal(portals: MarkerPortal[], id: string): MarkerPortal[] {
  if (!portals.some((portal) => portal.id === id)) {
    return portals
  }
  return portals.filter((portal) => portal.id !== id)
}

function handleMarkerLayerAdd(
  event: Leaflet.LayerEvent,
  contentByLayerId: Map<number, ReactNode>,
  setMarkerPortals: Dispatch<SetStateAction<MarkerPortal[]>>
): void {
  const layer = event.layer
  if (isPoiMarkerLayer(layer)) {
    return
  }

  const element = ((layer as unknown) as Partial<Leaflet.Marker>).getElement?.()
  if (!element) {
    return
  }

  const content = isMarkerCluster(layer) ? (
    <MapClusterMarker count={layer.getChildCount()} />
  ) : (
    contentByLayerId.get(Leaflet.stamp(layer))
  )
  if (!content) {
    return
  }

  const id = String(Leaflet.stamp(layer))
  setMarkerPortals((prev) => upsertMarkerPortal(prev, { id, container: element, content }))
}

function handleMarkerLayerRemove(
  event: Leaflet.LayerEvent,
  setMarkerPortals: Dispatch<SetStateAction<MarkerPortal[]>>
): void {
  if (isPoiMarkerLayer(event.layer)) {
    return
  }

  const id = String(Leaflet.stamp(event.layer))
  setMarkerPortals((prev) => removeMarkerPortal(prev, id))
}

function isPoiMarkerLayer(layer: Leaflet.Layer): boolean {
  return layer.options?.pane === POI_MARKERS_PANE
}

function createLeafletMarker(marker: MapMarkerModel, options?: Leaflet.MarkerOptions): Leaflet.Marker {
  const [width, height] = marker.size ?? DEFAULT_MARKER_SIZE
  const [anchorX, anchorY] = marker.anchor ?? [width / 2, height]
  const [lng, lat] = marker.coordinates

  const icon = Leaflet.divIcon({
    className: 'leaflet-map-marker',
    html: '',
    iconSize: [width, height],
    iconAnchor: [anchorX, anchorY],
  })

  return Leaflet.marker([lat, lng], { icon, keyboard: false, ...options })
}

function createPoiMarkersLayer(map: Leaflet.Map): Leaflet.LayerGroup {
  map.createPane(POI_MARKERS_PANE).style.zIndex = POI_MARKERS_PANE_Z_INDEX
  return Leaflet.layerGroup()
}

function syncPoiMarkers(
  layer: Leaflet.LayerGroup,
  leafletMarkersById: Map<string, Leaflet.Marker>,
  markers: MapMarkerModel[],
  setPoiMarkerPortals: Dispatch<SetStateAction<MarkerPortal[]>>
): void {
  const nextIds = new Set(markers.map(({ id }) => id))

  removeStalePoiMarkers(layer, leafletMarkersById, nextIds)
  const addedPortals = addNewPoiMarkers(layer, leafletMarkersById, markers)

  setPoiMarkerPortals((prev) => syncPoiMarkerPortals(prev, markers, addedPortals))
}

function removeStalePoiMarkers(
  layer: Leaflet.LayerGroup,
  leafletMarkersById: Map<string, Leaflet.Marker>,
  nextIds: Set<string>
): void {
  leafletMarkersById.forEach((leafletMarker, id) => {
    if (!nextIds.has(id)) {
      layer.removeLayer(leafletMarker)
      leafletMarkersById.delete(id)
    }
  })
}

function addNewPoiMarkers(
  layer: Leaflet.LayerGroup,
  leafletMarkersById: Map<string, Leaflet.Marker>,
  markers: MapMarkerModel[]
): MarkerPortal[] {
  const addedPortals: MarkerPortal[] = []
  markers.forEach((marker) => {
    if (leafletMarkersById.has(marker.id)) {
      return
    }
    const leafletMarker = createLeafletMarker(marker, { pane: POI_MARKERS_PANE, interactive: false })
    layer.addLayer(leafletMarker)
    leafletMarkersById.set(marker.id, leafletMarker)
    addedPortals.push({ id: marker.id, container: leafletMarker.getElement()!, content: marker.content })
  })
  return addedPortals
}

function syncPoiMarkerPortals(
  portals: MarkerPortal[],
  markers: MapMarkerModel[],
  addedPortals: MarkerPortal[]
): MarkerPortal[] {
  const contentById = new Map(markers.map(({ id, content }) => [id, content]))
  const keptPortals = portals
    .filter(({ id }) => contentById.has(id))
    .map((portal) => ({ ...portal, content: contentById.get(portal.id) }))
  return [...keptPortals, ...addedPortals]
}

function toMarkersBounds(markers: MapMarkerModel[]): Leaflet.LatLngBounds {
  return Leaflet.latLngBounds(markers.map(({ coordinates: [lng, lat] }) => [lat, lng] as [number, number]))
}

function toLatLngBounds(maxBounds: [[number, number], [number, number]]): Leaflet.LatLngBounds {
  return Leaflet.latLngBounds([
    [maxBounds[0][1], maxBounds[0][0]],
    [maxBounds[1][1], maxBounds[1][0]],
  ])
}

function createMap(
  container: HTMLDivElement,
  options: { center: [number, number]; zoom: number; bounds: Leaflet.LatLngBounds }
): Leaflet.Map {
  const [centerLng, centerLat] = options.center
  return Leaflet.map(container, {
    center: [centerLat, centerLng],
    zoom: options.zoom,
    maxZoom: 19,
    maxBounds: options.bounds,
  })
}

function hideLeafletBranding(map: Leaflet.Map): void {
  map.attributionControl.setPrefix(false)
}

function clampMinZoomToBounds(map: Leaflet.Map, bounds: Leaflet.LatLngBounds, minZoom?: number): () => void {
  const update = () => {
    const boundsMinZoom = map.getBoundsZoom(bounds, true)
    const targetMinZoom = minZoom !== undefined ? Math.max(minZoom, boundsMinZoom) : boundsMinZoom

    if (map.getZoom() < targetMinZoom) {
      map.setZoom(targetMinZoom, { animate: false })
    }

    map.setMinZoom(targetMinZoom)
  }
  update()
  map.on('resize', update)
  return () => map.off('resize', update)
}

function watchResize(element: HTMLElement, onResize: () => void): () => void {
  const observer = new ResizeObserver(onResize)
  observer.observe(element)
  return () => observer.disconnect()
}

function addBaseTileLayer(map: Leaflet.Map, tileSource: Source, backgroundColor: string): Leaflet.GridLayer {
  const layer = (leafletLayer({
    url: new PMTiles(tileSource) as LeafletLayerUrl,
    paintRules: PAINT_RULES,
    labelRules: LABEL_RULES,
    backgroundColor,
    attribution:
      '© Contribuidores do <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> © <a href="https://openmaptiles.org" target="_blank" rel="noopener">OpenMapTiles</a>',
    maxDataZoom: 14,
  }) as unknown) as Leaflet.GridLayer
  layer.addTo(map)
  return layer
}

function getBaseView(layer: Leaflet.GridLayer): View | undefined {
  const { views } = (layer as unknown) as { views?: unknown }
  return views instanceof Map ? views.get('') : undefined
}

function watchInitialLoad(layer: Leaflet.GridLayer, onInitialLoad: () => void): () => void {
  layer.once('load', onInitialLoad)
  return () => layer.off('load', onInitialLoad)
}

function watchTileLoading(layer: Leaflet.GridLayer, onLoadingChange: (isLoading: boolean) => void): () => void {
  const handleLoadStart = () => onLoadingChange(true)
  const handleLoadEnd = () => onLoadingChange(false)
  layer.on('loading', handleLoadStart)
  layer.on('load', handleLoadEnd)
  return () => {
    layer.off('loading', handleLoadStart)
    layer.off('load', handleLoadEnd)
  }
}

const createStyles = (theme: Theme, topOverlayHeight: number) => ({
  root: css`
    position: relative;
    z-index: 0;
    width: 100%;
    height: 100%;

    .leaflet-top {
      top: ${topOverlayHeight}px;
    }
  `,
  map: css`
    width: 100%;
    height: 100%;
    outline: 0;
    &:focus {
      box-shadow: ${focusBoxShadow(theme)};
    }
  `,
  clusterMarkerFocus: css`
    .leaflet-map-cluster-marker {
      outline: 0;
      border-radius: 50%;

      &:focus {
        box-shadow: ${focusBoxShadow(theme)};
      }
    }
  `,
})
